import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  DocumentSnapshot,
} from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import QRCode from 'qrcode';
import * as XLSX from 'xlsx';

export type { DocumentSnapshot };

const CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/dn9cspqmi/image/upload';
const UPLOAD_PRESET = 'ml_default';

export interface GuardData {
  id: string;
  guardId?: string;
  name?: string;
  fatherName?: string;
  cnic?: string;
  contactNo1?: string;
  contactNo2?: string;
  companyName?: string;
  salary?: string | number;
  dateOfRegistration?: Date | any;
  dateOfBirth?: Date | any;
  dateOfJoining?: Date | any;
  cnicExpiry?: Date | any;
  education?: string;
  currentAddress?: string;
  permanentAddress?: string;
  policeDistrictCurrent?: string;
  policeDistrictPermanent?: string;
  language?: string;
  married?: boolean;
  sameAddress?: boolean;
  emergencyContact?: {
    name?: string;
    contactNo?: string;
    address?: string;
    cnic?: string;
  };
  services?: {
    type?: 'civilian' | 'serviceRetired';
    unitNo?: string;
    experienceYears?: string;
  };
  questions?: {
    workedAsGuard?: boolean;
    companyName?: string;
    reasonOfLeaving?: string;
    apssaTrained?: boolean;
    workAnywhere?: boolean;
    armyCourtCase?: boolean;
  };
  references?: Array<{
    name?: string;
    address?: string;
    cnic?: string;
    contact?: string;
    relation?: string;
  }>;
  image?: string;
  link?: string;
  isActive?: boolean;
  inactiveReason?: string;
  inactiveDate?: string;
  statusUpdatedAt?: Date | any;
  statusUpdatedBy?: string;
  history?: Array<{
    client?: any;
    guard?: any;
    createdAt?: Date | any;
    endDate?: string;
    completedAt?: string;
    status?: string;
    postDate?: string;
    salary?: number;
  }>;
  currentDeployment?: {
    client?: any;
    guard?: any;
    createdAt?: Date | any;
    endDate?: string | null;
    postDate?: string;
    slotNumber?: number;
    status?: string;
    updatedAt?: Date | any;
    salary?: number;
  };
  currentOTDeployment?: {
    client?: any;
    guard?: any;
    createdAt?: Date | any;
    endDate?: string | null;
    postDate?: string;
    slotNumber?: number;
    status?: string;
    updatedAt?: Date | any;
    salary?: number;
  };
  lastDeploymentUpdate?: string;
  documents?: Array<{
    id: string;
    name: string;
    url: string;
    type: 'APSA' | 'Verification' | 'Other';
    expiryDate?: string;
    uploadedAt: string;
    uploadedBy: string;
    fileType?: string;
    fileSize?: number;
  }>;
}

export interface PaginationResult {
  guards: GuardData[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  firstDoc: DocumentSnapshot | null;
  lastDoc: DocumentSnapshot | null;
}

export interface ExportProgress {
  current: number;
  total: number;
}

export const convertToDate = (value: any): Date | null => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const date = new Date(value);
  return !isNaN(date.getTime()) ? date : null;
};

export const formatDateForExcel = (date: Date | null): string => {
  if (!date || !(date instanceof Date)) return '';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Fetch guards with pagination
export const fetchGuards = async (
  direction: 'first' | 'next' | 'prev' = 'first',
  cursor: DocumentSnapshot | null = null,
  pageStack: DocumentSnapshot[] = [],
  pageSize: number = 20,
  companyId?: string
): Promise<PaginationResult> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const guardCol = collection(db, 'guards', uid, 'companyGuards');
  let q;

  if (direction === 'first') {
    q = query(guardCol, orderBy('guardId'), limit(pageSize + 1));
  } else if (direction === 'next' && cursor) {
    q = query(guardCol, orderBy('guardId'), startAfter(cursor), limit(pageSize + 1));
  } else if (direction === 'prev' && pageStack.length > 0) {
    const prevCursor = pageStack[pageStack.length - 1];
    q = query(guardCol, orderBy('guardId'), startAfter(prevCursor), limit(pageSize + 1));
  } else {
    throw new Error('Invalid pagination direction or missing cursor');
  }

  const snap = await getDocs(q);
  const docs = snap.docs;
  const hasMore = docs.length > pageSize;
  const actualDocs = hasMore ? docs.slice(0, pageSize) : docs;

  const guards = actualDocs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as GuardData[];

  return {
    guards,
    hasNextPage: hasMore,
    hasPrevPage: pageStack.length > 0,
    firstDoc: actualDocs[0] || null,
    lastDoc: actualDocs[actualDocs.length - 1] || null,
  };
};

// Get total guard count
export const fetchGuardCount = async (companyId?: string): Promise<number> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) return 0;

  try {
    const guardCol = collection(db, 'guards', uid, 'companyGuards');
    const snap = await getDocs(guardCol);
    return snap.docs.length;
  } catch (error) {
    console.error('Error fetching guard count:', error);
    return 0;
  }
};

// Load all guards for export
export const loadAllGuardsForExport = async (companyId?: string): Promise<GuardData[]> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) return [];

  try {
    const guardCol = collection(db, 'guards', uid, 'companyGuards');
    const snap = await getDocs(guardCol);
    return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as GuardData[];
  } catch (error) {
    console.error('Error loading all guards:', error);
    return [];
  }
};

/** Paginated guards for attendance: one page only. Uses orderBy('guardId') for stable cursor. */
export const getGuardsPage = async (
  companyId: string,
  pageSize: number,
  startAfterDoc?: DocumentSnapshot | null
): Promise<{ guards: GuardData[]; lastDoc: DocumentSnapshot | null }> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) return { guards: [], lastDoc: null };

  const guardCol = collection(db, 'guards', uid, 'companyGuards');
  const q = startAfterDoc
    ? query(guardCol, orderBy('guardId'), startAfter(startAfterDoc), limit(pageSize))
    : query(guardCol, orderBy('guardId'), limit(pageSize));
  const snap = await getDocs(q);
  const guards = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as GuardData[];
  const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
  return { guards, lastDoc };
};

/** Total guard count for pagination (efficient server count). */
export const getGuardsTotalCount = async (companyId: string): Promise<number> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) return 0;
  try {
    const guardCol = collection(db, 'guards', uid, 'companyGuards');
    const snap = await getCountFromServer(guardCol);
    return snap.data().count ?? 0;
  } catch (e) {
    console.warn('getCountFromServer failed, falling back to fetchGuardCount:', e);
    return fetchGuardCount(companyId);
  }
};

// Update guard
export const updateGuard = async (guardId: string, data: Partial<GuardData>, companyId?: string): Promise<void> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const docRef = doc(db, 'guards', uid, 'companyGuards', guardId);
  const updateData: any = { ...data };

  // Convert dates properly
  if (updateData.dateOfRegistration instanceof Date) {
    updateData.dateOfRegistration = updateData.dateOfRegistration;
  }
  if (updateData.dateOfBirth instanceof Date) {
    updateData.dateOfBirth = updateData.dateOfBirth;
  }
  if (updateData.dateOfJoining instanceof Date) {
    updateData.dateOfJoining = updateData.dateOfJoining;
  }
  if (updateData.cnicExpiry instanceof Date) {
    updateData.cnicExpiry = updateData.cnicExpiry;
  }

  delete updateData.id;
  await updateDoc(docRef, updateData);
};

// Update guard status
export const updateGuardStatus = async (
  guardId: string,
  isActive: boolean,
  reason: string = '',
  inactiveDate: string = '',
  companyId?: string
): Promise<void> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const docRef = doc(db, 'guards', uid, 'companyGuards', guardId);
  await updateDoc(docRef, {
    isActive,
    inactiveReason: isActive ? '' : reason,
    inactiveDate: isActive ? '' : inactiveDate,
    statusUpdatedAt: new Date(),
    statusUpdatedBy: uid,
  });
};

// Bulk update salary for all guards
export const bulkUpdateSalary = async (
  salary: string | number,
  companyId?: string
): Promise<{ success: number; failed: number }> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  try {
    // Load all guards
    const allGuards = await loadAllGuardsForExport(companyId);

    let success = 0;
    let failed = 0;

    // Update each guard's salary
    for (const guard of allGuards) {
      if (!guard.id) {
        failed++;
        continue;
      }

      try {
        const docRef = doc(db, 'guards', uid, 'companyGuards', guard.id);
        await updateDoc(docRef, {
          salary: salary,
        });
        success++;
      } catch (error) {
        console.error(`Failed to update guard ${guard.id}:`, error);
        failed++;
      }
    }

    return { success, failed };
  } catch (error) {
    console.error('Error in bulk salary update:', error);
    throw error;
  }
};

// Upload image to Cloudinary
export const uploadImage = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);

  const response = await fetch(CLOUDINARY_URL, {
    method: 'POST',
    body: formData,
  });

  const data = await response.json();
  if (data.secure_url) {
    return data.secure_url;
  }
  throw new Error('Image upload failed');
};

// Generate QR Code
export const generateQRCode = async (data: string): Promise<string> => {
  return await QRCode.toDataURL(data, {
    width: 200,
    margin: 2,
    color: {
      dark: '#000000',
      light: '#FFFFFF',
    },
  });
};

// Export to Excel (without QR codes)
export const exportToExcelOnly = async (companyId?: string): Promise<string> => {
  const allData = await loadAllGuardsForExport(companyId);

  const workbook = XLSX.utils.book_new();
  const exportData = allData.map((guard) => ({
    'Guard ID': guard.guardId || '',
    'Name': guard.name || '',
    'Father Name': guard.fatherName || '',
    'CNIC': guard.cnic || '',
    'Contact No 1': guard.contactNo1 || '',
    'Contact No 2': guard.contactNo2 || '',
    'Company Name': guard.companyName || '',
    'Date of Registration': formatDateForExcel(convertToDate(guard.dateOfRegistration)),
    'Date of Birth': formatDateForExcel(convertToDate(guard.dateOfBirth)),
    'Education': guard.education || '',
    'Current Address': guard.currentAddress || '',
    'Permanent Address': guard.permanentAddress || '',
    'Police District Current': guard.policeDistrictCurrent || '',
    'Police District Permanent': guard.policeDistrictPermanent || '',
    'Language': guard.language || '',
    'Married': guard.married ? 'Yes' : 'No',
    'Same Address': guard.sameAddress ? 'Yes' : 'No',
    'Emergency Contact Name': guard.emergencyContact?.name || '',
    'Emergency Contact No': guard.emergencyContact?.contactNo || '',
    'Emergency Contact Address': guard.emergencyContact?.address || '',
    'Emergency Contact CNIC': guard.emergencyContact?.cnic || '',
    'Service Type': guard.services?.type || '',
    'Unit No': guard.services?.unitNo || '',
    'Experience Years': guard.services?.experienceYears || '',
    'Worked as Guard': guard.questions?.workedAsGuard ? 'Yes' : 'No',
    'Previous Company Name': guard.questions?.companyName || '',
    'Reason of Leaving': guard.questions?.reasonOfLeaving || '',
    'APSSA Trained': guard.questions?.apssaTrained ? 'Yes' : 'No',
    'Work Anywhere': guard.questions?.workAnywhere ? 'Yes' : 'No',
    'Army Court Case': guard.questions?.armyCourtCase ? 'Yes' : 'No',
    'Reference 1 Name': guard.references?.[0]?.name || '',
    'Reference 1 Address': guard.references?.[0]?.address || '',
    'Reference 1 CNIC': guard.references?.[0]?.cnic || '',
    'Reference 1 Contact': guard.references?.[0]?.contact || '',
    'Reference 1 Relation': guard.references?.[0]?.relation || '',
    'Reference 2 Name': guard.references?.[1]?.name || '',
    'Reference 2 Address': guard.references?.[1]?.address || '',
    'Reference 2 CNIC': guard.references?.[1]?.cnic || '',
    'Reference 2 Contact': guard.references?.[1]?.contact || '',
    'Reference 2 Relation': guard.references?.[1]?.relation || '',
    'Image URL': guard.image || '',
    'Public Link': guard.link || '',
    'QR Code Data': guard.link ? `https://guarding.dynamixzone.com${guard.link}` : '',
    'Status': guard.isActive !== false ? 'Active' : 'Inactive',
    'Inactive Reason': guard.inactiveReason || '',
    'Inactive Date': guard.inactiveDate ? new Date(guard.inactiveDate).toLocaleDateString() : '',
    'Status Updated At': guard.statusUpdatedAt
      ? convertToDate(guard.statusUpdatedAt)?.toLocaleString() || ''
      : '',
  }));

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const columnWidths = Array(47).fill({ wch: 20 });
  worksheet['!cols'] = columnWidths;
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Guards');

  const fileName = `Guards_Data_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, fileName);

  return fileName;
};

// Export with QR codes (ZIP file)
export const exportWithQRCodes = async (
  onProgress?: (progress: ExportProgress) => void,
  companyId?: string
): Promise<string> => {
  const allData = await loadAllGuardsForExport(companyId);
  const JSZip = await import('jszip');
  const zip = new JSZip.default();

  // Create Excel file
  const workbook = XLSX.utils.book_new();
  const exportData = allData.map((guard) => ({
    'Guard ID': guard.guardId || '',
    'Name': guard.name || '',
    'QR Code Data': guard.link ? `https://guarding.dynamixzone.com${guard.link}` : '',
    // ... (similar to exportToExcelOnly but simplified)
  }));

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Guards');
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  zip.file('Guards_Data.xlsx', excelBuffer);

  // Generate QR codes
  const qrFolder = zip.folder('QR_Codes');
  const guardsWithLinks = allData.filter((guard) => guard.link && guard.guardId);

  for (let i = 0; i < guardsWithLinks.length; i++) {
    const guard = guardsWithLinks[i];
    if (guard.link && guard.guardId) {
      try {
        const fullUrl = `https://guarding.dynamixzone.com${guard.link}`;
        const qrCodeDataUrl = await generateQRCode(fullUrl);
        const response = await fetch(qrCodeDataUrl);
        const blob = await response.blob();
        qrFolder?.file(`${guard.guardId}_QR.png`, blob);

        if (onProgress) {
          onProgress({ current: i + 1, total: guardsWithLinks.length });
        }
      } catch (error) {
        console.error('Error processing QR for guard:', guard.guardId, error);
      }
    }
  }

  // Generate and download
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const url = window.URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = url;
  const fileName = `Guards_Data_With_QR_Codes_${new Date().toISOString().split('T')[0]}.zip`;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return fileName;
};

